import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, OnInit, inject } from '@angular/core';
import { Catalog } from '@df/catalog/catalog.interfaces';
import { ISizeName } from '@df/core/service/client';
import { Basket } from '@df/session/api/basket/basket';
import { DfUiModule } from '@df/ui/df-ui.module';
import { DfCmsModal } from '@df/ui/modal/df-cms-modal.decorator';
import { NDfModal } from '@df/ui/modal/df-modal.interface';
import { ModalRef } from '@df/ui/modal/modal-ref';
import { MODAL_DATA } from '@df/ui/modal/modal.service';
import { MultisiteRedirectService } from '@df/ui/multisite/multisite-redirect.service';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { ICmsBlock } from '@icms/core/resource/block/icms-block';

export namespace NBasketCarryOverModal {
    export const blockId = 'basket-carry-over';
    export type TVariant = 'canCarry' | 'cannotCarry' | 'error';
    export type Block = ICmsBlock<Catalog.IBlockData<IData>>;
    export interface IData {
        title: string;
        subtitle: string;
        infoTitle: string;
        infoSubitle: string;
        errorTitle: string;
        errorSubtitle: string;
        variantPreview?: TVariant;
    }

    export interface IModalData {
        variant: TVariant;
        siteId: number | string;
        $event: Event;
        errorMessage?: string;
    }
}
@DfCmsModal({
    id: NBasketCarryOverModal.blockId,
    name: 'Basket Carry Over',
    config: (sizeId: ISizeName) => ({
        minWidth: '200px',
        width: sizeId !== 'S' ? '100%' : '100vw',
        maxWidth: sizeId !== 'S' ? '399px' : '91vw',
        minHeight: '200px',
        maxHeight: sizeId !== 'S' ? '95vh' : undefined,
        modalClass: 'p-a-0',
        hasBackdrop: true,
        scrollStrategy: 'noop'
    }),
    blockGroup: 'modals',
    priority: 20,
    reopenPolicy: NDfModal.ReopenPolicy.first,
    sizeRestriction: size => (size === 'S' ? ['S'] : ['M', 'L', 'X'])
})
@ICmsComponent<BasketCarryOverModalComponent>({
    id: NBasketCarryOverModal.blockId,
    block: {
        url: NBasketCarryOverModal.blockId
    },
    onDemandOnly: true,
    name: 'Basket Carry Over Popup',
    fields: [
        {
            key: 'can-carry-over',
            type: NICmsContentField.EFieldType.label,
            title: 'Can carry over'
        },
        {
            key: 'title',
            type: NICmsContentField.EFieldType.text,
            title: 'Title',
            required: true,
            default: 'You have items in your basket'
        },
        {
            key: 'subtitle',
            type: NICmsContentField.EFieldType.text,
            title: 'Subtitle',
            required: true,
            default: 'Would you like to carry over the available items?'
        },
        {
            key: 'cannot-carry-over',
            type: NICmsContentField.EFieldType.label,
            title: 'Cannot carry over'
        },
        {
            key: 'infoTitle',
            type: NICmsContentField.EFieldType.text,
            title: 'Info Title',
            required: true,
            default: 'Some items weren’t available in this region'
        },
        {
            key: 'infoSubtitle',
            type: NICmsContentField.EFieldType.text,
            title: 'Info Subtitle',
            required: true,
            default: 'Unfortunately not all products were available in this region and have been removed from your basket.'
        },
        {
            key: 'error',
            type: NICmsContentField.EFieldType.label,
            title: 'Error'
        },
        {
            key: 'errorTitle',
            type: NICmsContentField.EFieldType.text,
            title: 'Error Title',
            required: true,
            default: 'Something went wrong'
        },
        {
            key: 'errorSubtitle',
            type: NICmsContentField.EFieldType.text,
            title: 'Error Subtitle',
            required: true,
            default: 'Unfortunately we were unable to carry over your basket'
        },
        <NICmsContentField.IChoiceField>{
            key: 'variantPreview',
            type: NICmsContentField.EFieldType.choice,
            title: 'Variant Preview',
            description: 'Preview the different variants (only in the icms)',
            options: [
                { key: 'canCarry', title: 'canCarry' },
                { key: 'cannotCarry', title: 'cannotCarry' },
                { key: 'error', title: 'error' }
            ],
            default: 'canCarry'
        }
    ]
})
@Component({
    selector: 'basket-carry-over-modal',
    templateUrl: './basket-carry-over-modal.html',
    standalone: true,
    imports: [TranslationModule, ICmsClientModule, CommonModule, DfUiModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BasketCarryOverModalComponent extends UiComponent implements NICmsContentComponent.IComponent, OnInit {
    @ICmsContent({
        type: 'blockContent'
    })
    content: NBasketCarryOverModal.IData;

    constructor(
        @Inject(MODAL_DATA)
        public modalData: NBasketCarryOverModal.IModalData
    ) {
        super();
    }

    private modalRef = inject(ModalRef);
    private basket = inject(Basket);
    protected multisiteRedirectService = inject(MultisiteRedirectService);

    protected close(): void {
        this.modalRef.close();
    }

    protected accept(): void {
        const { siteId, $event } = this.modalData;
        const quote_id = this.basket.data?.quote_id;
        const currentDate = new Date();
        const expirationDate = new Date(currentDate.getTime() + 10 * 60000);
        const sessionStorageObject = {
            quote_id,
            expirationDate: expirationDate,
            targetSiteId: siteId
        };
        window.sessionStorage.setItem('basket-carry-over', JSON.stringify(sessionStorageObject));
        if (this.multisiteRedirectService.to(siteId)) {
            $event.preventDefault();
        }
    }

    protected getRedirectLink(siteId: number | string): string | undefined {
        return this.multisiteRedirectService.getLink(siteId);
    }
}
